import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Video } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useCallsAvailable } from "@/components/call/CallButton";
import { CallCountdown } from "@/components/call/Countdown";
import { CalendarButtons, ScheduleCall, formatLocal } from "@/components/call/ScheduleCall";

export const GroupCallBanner = ({ expId }) => {
  const navigate = useNavigate();
  const available = useCallsAvailable();
  const [status, setStatus] = useState({ call_id: null, participants: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!available) return undefined;
    const poll = () => !document.hidden && api.get(`/calls/experience/${expId}`).then(({ data }) => setStatus(data)).catch(() => {});
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [expId, available]);

  if (!available) return null;
  const join = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/calls/experience/${expId}`); navigate(`/call/${data.call_id}`, { state: data }); }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  const people = status.participants;
  const live = people.length > 0;
  return (
    <div className={`mb-6 flex flex-wrap items-center gap-4 rounded-xl border p-4 ${live ? "border-primary/60 bg-primary/10" : "border-border bg-card"}`} data-testid="group-call-banner">
      <span className={`h-2.5 w-2.5 rounded-full ${live ? "animate-pulse bg-primary" : "bg-muted-foreground/50"}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold" data-testid="group-call-title">{live ? "Group call in progress" : "Group planning call"}</p>
        <p className="text-sm text-foreground/75" data-testid="group-call-participants">
          {live ? `In the call: ${people.map((p) => p.name).join(", ")}` : "Host, co-hosts and guests can meet here on video to plan together."}
        </p>
        {status.scheduled && <p className="mt-1 text-sm text-primary" data-testid="group-call-scheduled">Scheduled: {formatLocal(status.scheduled.at)}{status.scheduled.note ? ` · ${status.scheduled.note}` : ""}</p>}
        {status.scheduled && <CallCountdown at={status.scheduled.at} />}
        {status.scheduled && <CalendarButtons scheduled={status.scheduled} />}
      </div>
      {status.is_host && <ScheduleCall key={status.scheduled?.at || "none"} expId={expId} scheduled={status.scheduled} onSaved={(sc) => setStatus((s) => ({ ...s, scheduled: sc }))} />}
      <button type="button" className="btn-primary h-11 px-5 text-sm" disabled={busy} onClick={join} data-testid="group-call-join-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Join group call
      </button>
    </div>
  );
};
